import {partition} from 'lodash';
import React, {FC} from 'react';
import styled from 'styled-components';

import {Trans} from '../../../../../libs/shared/core/src/helpers/internationalization/react';
import {CalendarsPanelId} from '../../../../../libs/shared/core/src/store/panels/panelStateBase';
import {Button} from '../../../../../libs/shared/ui/src/components/buttons/button';
import {ButtonVariantsEnum} from '../../../../../libs/shared/ui/src/components/buttons/buttonConstants';
import {VisualParticipantModel} from '../../../../models/api/visualParticipantModel';
import {CalendarEventParticipantsList} from '../../participants/calendarEventParticipantsList';
import {VisualParticipantsDropdownBox} from './visualParticipantsDropdownBox';

/*
 * Props.
 */

interface CalendarEventParticipantsEditorProps {
  panelId: CalendarsPanelId;
  visualParticipants: ReadonlyArray<VisualParticipantModel>;
  isUnsavedDraft: boolean;
  organizerAddress?: string;
  /* Change handler for participants. If none is specified, the editor becomes read-only. */
  onVisualParticipantsChange?: (arg: ReadonlyArray<VisualParticipantModel>) => void;
  /* A list of participants that cannot be edited, if for example the user can invite new participants but not edit existing ones. */
  readonlyContactUids?: ReadonlyArray<string>;
  onViewGuestAvailabilityClick?: (arg: ReadonlyArray<VisualParticipantModel>) => void;
}

/*
 * Styles.
 */

const StyledVisualParticipantsDropdownBox = styled(VisualParticipantsDropdownBox)`
  margin: 0px;
`;

const StyledListWrapperDiv = styled.div`
  margin-top: 8px;
  margin-bottom: 8px;
`;

const StyledAvailabilityButtonWrapperDiv = styled.div`
  display: flex;
`;

/*
 * Component.
 */

export const CalendarEventParticipantsEditor: FC<CalendarEventParticipantsEditorProps> = (props) => {
  const hasExistingAttendees = props.visualParticipants.filter((p) => p.isAttendee).length > 0;
  return (
    <div>
      {props.onVisualParticipantsChange && (
        <StyledVisualParticipantsDropdownBox
          panelId={props.panelId}
          hasExistingAttendees={hasExistingAttendees}
          onVisualParticipantsSelect={(newVisualParticipants) =>
            onVisualParticipantsSelect(props, newVisualParticipants)
          }
          organizerAddress={props.organizerAddress}
          isUnsavedDraft={props.isUnsavedDraft}
        />
      )}
      {hasExistingAttendees && (
        <StyledListWrapperDiv>
          <CalendarEventParticipantsList
            panelId={props.panelId}
            visualParticipants={props.visualParticipants}
            onVisualParticipantsChange={props.onVisualParticipantsChange}
            readonlyContactUids={props.readonlyContactUids}
            shouldShowAllParticipants
            isInEventEditor
          />
        </StyledListWrapperDiv>
      )}
      {maybeRenderSeeAvailabilityButton(props)}
    </div>
  );
};

function onVisualParticipantsSelect(
  props: CalendarEventParticipantsEditorProps,
  newVisualParticipants: ReadonlyArray<VisualParticipantModel>,
) {
  const {visualParticipants, onVisualParticipantsChange} = props;

  if (!onVisualParticipantsChange) {
    return null;
  }

  // If any of the newVisualParticipants already exist in visualParticipants, ensure that they're marked as attendee. Otherwise add them as new participants.
  const existingContactUids = visualParticipants.map((p) => p.contact.uid);
  const [participantsToUpdate, participantsToAdd] = partition(newVisualParticipants, (p) =>
    existingContactUids.includes(p.contact.uid),
  );
  const updatedParticipants = visualParticipants.map((p) => {
    if (participantsToUpdate.find((pu) => pu.contact.uid === p.contact.uid) && !p.isAttendee) {
      return {
        ...p,
        isAttendee: true,
      };
    }

    return p;
  });

  return onVisualParticipantsChange([...updatedParticipants, ...participantsToAdd]);
}

function maybeRenderSeeAvailabilityButton(props: CalendarEventParticipantsEditorProps) {
  // Show the "View guest availability" button if:
  // 1) The handler is provided 2) We are able edit participants
  // 3) We're not editing an unsaved draft event 4) There are between 2 and 20 participants.
  const shouldShowSeeAvailabilityButton =
    props.onViewGuestAvailabilityClick &&
    props.onVisualParticipantsChange &&
    !props.isUnsavedDraft &&
    props.visualParticipants.length > 1 &&
    props.visualParticipants.length < 20;

  if (!shouldShowSeeAvailabilityButton) {
    return null;
  }

  return (
    <StyledAvailabilityButtonWrapperDiv>
      <Button
        variant={ButtonVariantsEnum.LINK}
        onClick={() =>
          props.onViewGuestAvailabilityClick && props.onViewGuestAvailabilityClick(props.visualParticipants)
        }
      >
        <Trans id="calendar-event-participants-see-availability">View guest availability</Trans>
      </Button>
    </StyledAvailabilityButtonWrapperDiv>
  );
}
